import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon } from 'lucide-react';
import { RoundingRule, RoundingScope } from './RoundingConfiguration';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
import type { OrgUnit } from '../sharedFinancialData';
interface RoundingFormProps {
  rule: RoundingRule | null;
  onSave: (rule: Omit<RoundingRule, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
}
export function RoundingForm({
  rule,
  onSave,
  onCancel
}: RoundingFormProps) {
  const [formData, setFormData] = useState<Omit<RoundingRule, 'id' | 'createdAt' | 'updatedAt'>>({
    name: rule?.name || '',
    description: rule?.description || '',
    roundingType: rule?.roundingType || 'Nearest',
    interval: rule?.interval || 0.01,
    scope: rule?.scope || 'Global',
    scopeId: rule?.scopeId,
    scopeName: rule?.scopeName,
    scopePath: rule?.scopePath,
    active: rule?.active !== undefined ? rule.active : true
  });
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Sample vendors for dropdown
  const [vendors] = useState([{
    id: 'vendor-001',
    name: 'Office Depot'
  }, {
    id: 'vendor-002',
    name: 'Dell Technologies'
  }, {
    id: 'vendor-003',
    name: 'PrintWorks Inc.'
  }, {
    id: 'vendor-004',
    name: 'Acme Construction'
  }, {
    id: 'vendor-005',
    name: 'Global Logistics Partners'
  }]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (name === 'scope') {
      // Reset scope-related fields when scope changes
      setFormData({
        ...formData,
        scope: value as RoundingScope,
        scopeId: undefined,
        scopeName: undefined,
        scopePath: undefined
      });
      setSelectedOrgUnit(null);
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    const sanitizedValue = value.replace(/[^\d.]/g, '');
    const numberValue = sanitizedValue === '' ? '' : parseFloat(sanitizedValue);
    if (!isNaN(numberValue as number)) {
      setFormData({
        ...formData,
        [name]: numberValue
      });
    }
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setSelectedOrgUnit(orgUnit);
    setFormData({
      ...formData,
      scopeId: orgUnitId,
      scopeName: orgUnit?.name,
      scopePath: orgUnit?.path
    });
    if (errors.scopeId) {
      setErrors({
        ...errors,
        scopeId: ''
      });
    }
  };
  const handleVendorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      value
    } = e.target;
    const vendor = vendors.find(v => v.id === value);
    setFormData({
      ...formData,
      scopeId: value || undefined,
      scopeName: vendor?.name,
      scopePath: undefined
    });
    if (errors.scopeId) {
      setErrors({
        ...errors,
        scopeId: ''
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (formData.interval <= 0) {
      newErrors.interval = 'Interval must be greater than 0';
    }
    if (formData.scope === 'OrgUnit' && !formData.scopeId) {
      newErrors.scopeId = 'Organisation Unit is required when scope is OrgUnit';
    }
    if (formData.scope === 'Vendor' && !formData.scopeId) {
      newErrors.scopeId = 'Vendor is required when scope is Vendor';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10842">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-10843">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10844">
          {rule ? 'Edit Rounding Rule' : 'Create Rounding Rule'}
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-10845">
          {rule ? 'Update the details of an existing rounding rule' : 'Define a new rounding rule for use across the system'}
        </p>
      </div>

      <form onSubmit={handleSubmit} data-id="element-10846">
        <div className="px-4 py-5 sm:p-6" data-id="element-10847">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-10848">
            {/* Scope */}
            <div className="sm:col-span-3" data-id="element-10849">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-10850">
                Scope <span className="text-red-500" data-id="element-10851">*</span>
              </label>
              <div className="mt-1" data-id="element-10852">
                <select id="scope" name="scope" value={formData.scope} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-10853">
                  <option value="Global" data-id="element-10854">Whole Application</option>
                  <option value="Vendor" data-id="element-10855">Specific Vendor</option>
                  <option value="OrgUnit" data-id="element-10856">Specific Organisation Unit</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-10857">
                {formData.scope === 'Global' && 'Default rule for all entities'}
                {formData.scope === 'Vendor' && 'Overrides Global and OrgUnit rules for this vendor'}
                {formData.scope === 'OrgUnit' && 'Overrides Global rule for this organisation unit'}
              </p>
            </div>

            {/* Vendor Selector - Only shown when scope is Vendor */}
            {formData.scope === 'Vendor' && <div className="sm:col-span-3" data-id="element-10858">
                <label htmlFor="vendorId" className="block text-sm font-medium text-gray-700" data-id="element-10859">
                  Vendor <span className="text-red-500" data-id="element-10860">*</span>
                </label>
                <div className="mt-1" data-id="element-10861">
                  <select id="vendorId" name="vendorId" value={formData.scopeId || ''} onChange={handleVendorChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.scopeId ? 'border-red-300' : ''}`} data-id="element-10862">
                    <option value="" data-id="element-10863">Select a vendor</option>
                    {vendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-10864">
                        {vendor.name}
                      </option>)}
                  </select>
                  {errors.scopeId && <p className="mt-1 text-sm text-red-600" data-id="element-10865">
                      {errors.scopeId}
                    </p>}
                </div>
              </div>}

            {/* OrgUnit Selector - Only shown when scope is OrgUnit */}
            {formData.scope === 'OrgUnit' && <div className="sm:col-span-6" data-id="element-10866">
                <OrgUnitSelector value={formData.scopeId || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} error={errors.scopeId} data-id="element-10867" />
              </div>}

            <div className="sm:col-span-4" data-id="element-10868">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-10869">
                Name <span className="text-red-500" data-id="element-10870">*</span>
              </label>
              <div className="mt-1" data-id="element-10871">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} placeholder="e.g., Round to Nearest Cent" data-id="element-10872" />
                {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-10873">{errors.name}</p>}
              </div>
            </div>

            <div className="sm:col-span-6" data-id="element-10874">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-10875">
                Description
              </label>
              <div className="mt-1" data-id="element-10876">
                <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Optional description of the rounding rule" data-id="element-10877" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-10878">
              <label htmlFor="roundingType" className="block text-sm font-medium text-gray-700" data-id="element-10879">
                Rounding Type <span className="text-red-500" data-id="element-10880">*</span>
              </label>
              <div className="mt-1" data-id="element-10881">
                <select id="roundingType" name="roundingType" value={formData.roundingType} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-10882">
                  <option value="Nearest" data-id="element-10883">Nearest</option>
                  <option value="Up" data-id="element-10884">Up</option>
                  <option value="Down" data-id="element-10885">Down</option>
                  <option value="Swedish" data-id="element-10886">Swedish</option>
                  <option value="Truncate" data-id="element-10887">Truncate</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-10888">
                {formData.roundingType === 'Nearest' && 'Rounds to nearest interval (standard rounding)'}
                {formData.roundingType === 'Up' && 'Always rounds up to next interval'}
                {formData.roundingType === 'Down' && 'Always rounds down to previous interval'}
                {formData.roundingType === 'Swedish' && 'Special rounding for cash transactions'}
                {formData.roundingType === 'Truncate' && 'Removes decimal places without rounding'}
              </p>
            </div>

            <div className="sm:col-span-3" data-id="element-10889">
              <label htmlFor="interval" className="block text-sm font-medium text-gray-700" data-id="element-10890">
                Interval <span className="text-red-500" data-id="element-10891">*</span>
              </label>
              <div className="mt-1" data-id="element-10892">
                <input type="text" name="interval" id="interval" value={formData.interval} onChange={handleCurrencyChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.interval ? 'border-red-300' : ''}`} placeholder="0.01" pattern="^\d*(\.\d{0,6})?$" data-id="element-10893" />
                {errors.interval && <p className="mt-1 text-sm text-red-600" data-id="element-10894">{errors.interval}</p>}
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-10895">
                Common values: 0.01 (cent), 0.05 (nickel), 0.10 (dime), 1.00
                (dollar)
              </p>
            </div>

            <div className="sm:col-span-6" data-id="element-10896">
              <div className="flex items-start" data-id="element-10897">
                <div className="flex items-center h-5" data-id="element-10898">
                  <input id="active" name="active" type="checkbox" checked={formData.active} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-10899" />
                </div>
                <div className="ml-3 text-sm" data-id="element-10900">
                  <label htmlFor="active" className="font-medium text-gray-700" data-id="element-10901">
                    Active
                  </label>
                  <p className="text-gray-500" data-id="element-10902">
                    When checked, this rounding rule will be available for use
                    in the system
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-10903">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10904">
            <XIcon className="h-4 w-4 mr-1.5" data-id="element-10905" />
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10906">
            <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-10907" />
            {rule ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </form>
    </div>;
}